import { useEffect, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Knowledge-specific WebSocket hook.
 * Wraps unified WebSocket with knowledge page update handling.
 *
 * @param {Function} onItemUpdated - Callback when an item is updated (itemId, itemData)
 * @param {Function} onCollectionUpdated - Callback when a collection is updated (collectionId, collectionData)
 * @param {Function} onImportProgress - Callback when import progress is updated (progressData)
 * @param {Function} onImportCompleted - Callback when a document import completes (importData)
 * @returns {Object} Knowledge WebSocket state
 */
export function useKnowledgeWebSocket({ onItemUpdated, onCollectionUpdated, onImportProgress, onImportCompleted }) {
  const ws = useWebSocket();

  // Subscribe to knowledge channel
  useEffect(() => {
    if (!ws.isConnected) return;

    ws.subscribe('knowledge');

    return () => {
      ws.unsubscribe('knowledge');
    };
    // subscribe/unsubscribe use refs internally and are safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.isConnected]);

  // Register message handlers
  useEffect(() => {
    const handleItemUpdated = (data) => {
      if (onItemUpdated && data.item_id && data.item) {
        // Pass the entire item data including any error field
        const itemData = { ...data.item };
        if (data.error) {
          itemData.error = data.error;
        }
        onItemUpdated(data.item_id, itemData);
      }
    };

    const handleCollectionUpdated = (data) => {
      if (onCollectionUpdated && data.collection_id && data.collection) {
        onCollectionUpdated(data.collection_id, data.collection);
      }
    };

    const handleImportProgress = (data) => {
      if (onImportProgress) {
        onImportProgress(data);
      }
    };

    const handleImportCompleted = (data) => {
      if (onImportCompleted) {
        onImportCompleted(data);
      }
    };

    // Register handlers
    const cleanupFns = [
      ws.on('item_updated', handleItemUpdated),
      ws.on('collection_updated', handleCollectionUpdated),
      ws.on('import_progress', handleImportProgress),
      ws.on('import_completed', handleImportCompleted),
    ];

    return () => {
      // Cleanup all handlers
      cleanupFns.forEach(cleanup => cleanup());
    };
    // ws.on uses refs internally and is safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onItemUpdated, onCollectionUpdated, onImportProgress, onImportCompleted]);

  return {
    isConnected: ws.isConnected,
    error: ws.error,
  };
}
